// Workstream: growth. REST calls for the event-kit form and the admin Growth tab.
import { getAdminToken, getDeviceId, saveAdminToken } from '../../lib/storage';

async function request<T>(path: string, init: { method?: string; json?: unknown; admin?: boolean } = {}): Promise<T> {
  const headers: Record<string, string> = { 'x-device-id': getDeviceId() };
  if (init.json !== undefined) headers['content-type'] = 'application/json';
  if (init.admin) headers.authorization = `Bearer ${getAdminToken()}`;
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { method: init.method ?? 'GET', headers, body: init.json !== undefined ? JSON.stringify(init.json) : undefined });
  } catch {
    throw new Error('Could not reach the server. Check your connection.');
  }
  const body = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
  if (!res.ok) {
    if (res.status === 401 && init.admin) saveAdminToken(null);
    if (res.status === 429) throw new Error('That was a lot of tries. Give it a few minutes.');
    throw new Error((body as { error?: string } | null)?.error ?? `Request failed (${res.status}).`);
  }
  return body as T;
}

export type EventType = 'sangeet' | 'wedding' | 'student' | 'diaspora' | 'corporate' | 'other';

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  sangeet: 'Sangeet or mehndi',
  wedding: 'Wedding or reception',
  student: 'Student association',
  diaspora: 'Community or diaspora group',
  corporate: 'Office or corporate event',
  other: 'Something else',
};

export interface EnquiryInput {
  name: string;
  email: string;
  org: string;
  eventType: EventType;
  eventDate: string;
  guests: string;
  message: string;
  website: string;
}

export interface Enquiry extends Omit<EnquiryInput, 'eventType' | 'website'> {
  id: string;
  kind: string;
  handled: boolean;
  createdAt: string;
}

export interface GrowthSummary {
  totals: { devices30: number; devices7: number; pageViews30: number; games30: number; finished30: number; enquiriesOpen: number };
  daily: { day: string; devices: number; started: number; finished: number }[];
  funnel: { visited: number; inRoom: number; started: number; finished: number };
  retention: { devicesMultiDay: number; devicesPlayed: number; hostedRooms: number; hostsReturned: number };
  landingPages: { path: string; views: number }[];
  referrers: { source: string; views: number }[];
  ctas: { cta: string; clicks: number }[];
  errors: { message: string; count: number; lastSeen: string; path: string | null; stack: string | null }[];
  enquiries: Enquiry[];
}

export const growthApi = {
  sendEnquiry: (input: EnquiryInput) => request<{ ok: true }>('/contact', { method: 'POST', json: input }),
  summary: () => request<GrowthSummary>('/admin/growth/summary', { admin: true }),
  setHandled: (id: string, handled: boolean) => request<{ ok: true }>(`/admin/growth/enquiries/${id}`, { method: 'PATCH', json: { handled }, admin: true }),
};
